import { updateUserSidebarSettings } from "../../lib/serverComm";
import { produce } from "immer";
import { ActionType } from "./authContext";
import type {
  AuthenticatedContextData,
  AuthenticationContextData,
} from "@/types/authContext";

export const reducer = (
  state: AuthenticationContextData,
  action: ActionType,
): AuthenticationContextData => {
  // setting the user data is the only action that can be called without the user being logged in
  if (state.userData === null) {
    return unauthenticatedReducer(state, action);
  } else {
    return authenticatedReducer(state as AuthenticatedContextData, action);
  }
};

const unauthenticatedReducer = (
  state: AuthenticationContextData,
  action: ActionType,
): AuthenticationContextData => {
  switch (action.type) {
    case "setUserAction":
      return produce(state, (draft) => {
        draft.userData = action.payload;
      });
    case "setDeepInsightsEnabledAction":
      return produce(state, (draft) => {
        draft.deepInsightsState.enabled = false;
      });
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
};

const authenticatedReducer = (
  state: AuthenticatedContextData,
  action: ActionType,
): AuthenticationContextData => {
  switch (action.type) {
    case "addChatAction":
      return produce(state, (draft) => {
        draft.chatState.sidebarChats.data.unshift(action.payload);
      });
    case "openNewDeckModal":
      return produce(state, (draft) => {
        draft.modalState.isOpen = true;
        draft.modalState.type = "new-deck";
      });
    case "closeModalAction":
      return produce(state, (draft) => {
        draft.modalState.isOpen = false;
        draft.modalState.type = null;
      });
    case "removePendingChatAction":
      return produce(state, (draft) => {
        delete draft.chatState.pendingInitialChats[action.payload];
      });
    case "setCustomWorkspacesAction":
      return produce(state, (draft) => {
        draft.customWorkspaceState.customWorkspaces.data = action.payload;
      });
    case "setPendingChatsAction":
      return produce(state, (draft) => {
        draft.chatState.pendingInitialChats = {
          ...draft.chatState.pendingInitialChats,
          ...action.payload,
        };
      });
    case "setSidebarChatsAction":
      return produce(state, (draft) => {
        draft.chatState.sidebarChats.data = action.payload;
      });
    case "setSidebarCustomWorkspacesAction":
      return produce(state, (draft) => {
        draft.customWorkspaceState.sidebarCustomWorkspaces.data =
          action.payload;
      });
    case "setSideBarCustomWorkpacesParamsSort":
      return produce(state, (draft) => {
        draft.customWorkspaceState.sidebarCustomWorkspaces.params.sort =
          action.payload;
      });
    case "setUserAction":
      return produce(state, (draft) => {
        draft.userData = action.payload;
      });
    case "setDeepInsightsEnabledAction":
      return produce(state, (draft) => {
        draft.deepInsightsState.enabled = action.payload;
      });
    case "updateUserSidebarSettingsAction":
      updateUserSidebarSettings(action.payload); // naughty side effect we don't care about
      return produce(state, (draft) => {
        if (draft.userData) {
          draft.userData.sidebar_settings = action.payload;
        }
      });
    case "updateUserOnboardingAction":
      return produce(state, (draft) => {
        if (draft.userData) {
          draft.userData.onboarded = action.payload;
        }
      });
    case "setSidebarReportsAction":
      return produce(state, (draft) => {
        draft.reportState.sidebarReports.data = action.payload;
      });
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
};
